import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "./config";

export const cohortsApi = {
  // Get cohorts by user ID (author)
  getCohortsByUserId: async (userId) => {
    const response = await axiosInstance.get(`/cohorts/user/${userId}`);
    return response.data;
  },

  // Get cohort by ID
  getCohortById: async (cohortId) => {
    const response = await axiosInstance.get(`/cohorts/${cohortId}`);
    return response.data;
  },

  // Create a new cohort
  createCohort: async (authorId, name) => {
    const response = await axiosInstance.post("/cohorts", { authorId, name });
    return response.data;
  },

  // Delete a cohort
  deleteCohort: async (cohortId) => {
    const response = await axiosInstance.delete(`/cohorts/${cohortId}`);
    return response.data;
  },

  // Update cohort status
  updateCohortStatus: async (cohortId, status) => {
    const response = await axiosInstance.put(`/cohorts/${cohortId}/status`, {
      status,
    });
    return response.data;
  },

  updateCohortName: async (cohortId, name) => {
    const response = await axiosInstance.put(`/cohorts/${cohortId}/name`, {
      name,
    });
    return response.data;
  },

  // Join a cohort
  joinCohort: async (joinCode, userId, name = null) => {
    try {
      const payload = { joinCode };

      if (userId) {
        payload.userId = userId;
      }
      if (name !== null && name !== undefined) {
        payload.name = name;
      }

      const response = await axiosInstance.post("/cohorts/join", payload);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get cohort sessions
  getCohortSessions: async (cohortId) => {
    const response = await axiosInstance.get(`/cohorts/${cohortId}/sessions`);
    return response.data;
  },
  
  // Get aggregated cohort responses (counts by competency)
  getCohortResponses: async (cohortId, { completedOnly = true } = {}) => {
    const response = await axiosInstance.get(`/cohorts/${cohortId}/responses`, {
      params: { completedOnly },
    });
    return response.data;
  },

  // Get user's session count in a cohort
  getUserSessionCount: async (cohortId, userId) => {
    const response = await axiosInstance.get(`/cohorts/${cohortId}/user/${userId}/session-count`);
    return response.data;
  },

  // Search sessions across all of an author's archived cohorts by participant username
  searchArchivedSessionsByUsername: async (userId, username) => {
    const response = await axiosInstance.get(`/cohorts/user/${userId}/archived-sessions/search`, {
      params: { username },
    });
    return response.data;
  },
};

// Hook to get cohorts by user ID
export const useCohortsByUserId = (userId) => {
  return useQuery({
    queryKey: ["cohorts", "user", userId],
    queryFn: () =>
      userId ? cohortsApi.getCohortsByUserId(userId) : Promise.resolve([]),
    enabled: !!userId,
  });
};

export const useCohortById = (cohortId) => {
  return useQuery({
    queryKey: ["cohort", cohortId],
    queryFn: () =>
      cohortId ? cohortsApi.getCohortById(cohortId) : Promise.resolve(null),
    enabled: !!cohortId,
  });
};

export const useCreateCohort = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ authorId, name }) => cohortsApi.createCohort(authorId, name),
    onSuccess: (newCohort) => {
      queryClient.invalidateQueries({
        queryKey: ["cohorts"],
        exact: false,
      });
    },
  });
};

export const useDeleteCohort = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cohortId) => cohortsApi.deleteCohort(cohortId),
    onSuccess: (deletedCohort) => {
      queryClient.invalidateQueries({
        queryKey: ["cohorts"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["cohort", deletedCohort.id],
        exact: true,
      });
    },
  });
};

export const useUpdateCohortStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cohortId, status }) =>
      cohortsApi.updateCohortStatus(cohortId, status),
    onSuccess: (updatedCohort) => {
      queryClient.invalidateQueries({
        queryKey: ["cohorts"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["cohort", updatedCohort.id],
        exact: true,
      });
    },
  });
};

export const useUpdateCohortName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cohortId, name }) =>
      cohortsApi.updateCohortName(cohortId, name),
    onSuccess: (updatedCohort) => {
      queryClient.invalidateQueries({
        queryKey: ["cohorts"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ["cohort", updatedCohort.id],
        exact: true,
      });
    },
  });
};

export const useJoinCohort = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ joinCode, userId, name }) =>
      cohortsApi.joinCohort(joinCode, userId, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cohorts"] });
    },
  });
};

// Hook to get sessions for a cohort
export const useCohortSessions = (cohortId) => {
  return useQuery({
    queryKey: ["cohort", cohortId, "sessions"],
    queryFn: () =>
      cohortId ? cohortsApi.getCohortSessions(cohortId) : Promise.resolve([]),
    enabled: !!cohortId,
  });
};

// Search across an author's archived cohorts by participant username. A
// mutation (not a query) since it's an explicit, on-demand search action
// rather than data a page should auto-load/cache by key.
export const useSearchArchivedSessionsByUsername = () => {
  return useMutation({
    mutationFn: ({ userId, username }) =>
      cohortsApi.searchArchivedSessionsByUsername(userId, username),
  });
};

// Hook to get cohort responses
export const useCohortResponses = (cohortId, options = {}) => {
  const { completedOnly = true } = options;
  return useQuery({
    queryKey: ["cohort", cohortId, "responses", { completedOnly }],
    queryFn: () =>
      cohortId
        ? cohortsApi.getCohortResponses(cohortId, { completedOnly })
        : Promise.resolve({
            competency_counts: [],
            session_count: 0,
            response_count: 0,
            unique_competencies: 0,
          }),
    enabled: !!cohortId,
  });
};

// Hook to get user's session count in a cohort
export const useUserSessionCount = (cohortId, userId) => {
  return useQuery({
    queryKey: ["cohort", cohortId, "user", userId, "session-count"],
    queryFn: () =>
      cohortId && userId
        ? cohortsApi.getUserSessionCount(cohortId, userId)
        : Promise.resolve({ sessionCount: 0, maxSessions: 3, remainingSessions: 3 }),
    enabled: !!cohortId && !!userId,
  });
};
